import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlaceEntity, PlaceRequestEntity } from 'src/entities/placeEntity';
import { fetchAllPages, PagedResult } from 'src/utils/fetchAllPages';

const buildRequestPath: (page: number, withData: boolean) => string = (
  page,
  withData,
) => [`/api/v2/places?page=${page}`, withData ? 'with_data=1' : null].filter((maybe) => !!maybe).join('&');

const fetchPlacesPage = async (page: number): Promise<PagedResult<PlaceEntity>> => {
  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number,
    data: PlaceEntity[],
  }> = await axios.get(buildRequestPath(page, true));

  return { items: response.data.data, totalPagesCount: response.data.total_pages_count };
};

export const useQueryPlaces = (enabled: boolean) => useQuery({
  queryKey: ['places'],
  queryFn: () => fetchAllPages(fetchPlacesPage),
  enabled,
  staleTime: Infinity,
  // 再試行は fetchAllPages がページ単位で行う。ここで retry すると
  // 成功済みのページまで取り直してしまうため無効化する。
  retry: false,
});

export const useMutationPlace = () => {
  const queryClient = useQueryClient();
  return {
    addPlace: useMutation({
      mutationFn: (place: PlaceRequestEntity) => axios.post('/api/v2/places', place),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['places'] })
    }),
    updatePlace: useMutation({
      mutationFn: (place: PlaceEntity) => axios.put(`/api/v2/places/${place.id}`, place),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['places'] })
    }),
    deletePlaces: useMutation({
      mutationFn: (ids: number[]) => axios.delete('/api/v2/places/bulk', { data: { ids } }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['places'] })
    })
  };
};
